import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '资讯', to: '/news' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to.split('?')[0]);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? 'bg-primary-950/95 backdrop-blur' : 'bg-primary-950'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-primary-950">
              <i className="ri-film-line text-xl" />
            </span>
            <span className="text-lg font-bold tracking-wide text-background-50 font-heading">
              八戒影院
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? 'bg-primary-800 text-secondary-400'
                    : 'text-background-200 hover:bg-primary-900 hover:text-background-50'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={handleSearch}
              className="hidden lg:flex items-center gap-2 rounded-full bg-primary-900/80 px-3 py-1.5"
            >
              <i className="ri-search-line text-base text-background-300" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、剧集、资讯"
                className="w-40 bg-transparent text-sm text-background-50 placeholder:text-background-400 focus:outline-none"
              />
            </form>

            <button
              type="button"
              onClick={() => navigate('/search')}
              className="lg:hidden flex h-9 w-9 items-center justify-center rounded-md text-background-100 hover:bg-primary-900"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg" />
            </button>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden flex h-9 w-9 items-center justify-center rounded-md text-background-100 hover:bg-primary-900"
              aria-label="菜单"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-primary-800 bg-primary-950 px-4 pb-4 pt-2 animate-fade-in">
          <form onSubmit={handleSearch} className="mb-3 flex items-center gap-2 rounded-full bg-primary-900/80 px-3 py-2">
            <i className="ri-search-line text-base text-background-300" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、剧集、资讯"
              className="w-full bg-transparent text-sm text-background-50 placeholder:text-background-400 focus:outline-none"
            />
          </form>
          <nav className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2.5 text-sm font-medium ${
                  isActive(item.to)
                    ? 'bg-primary-800 text-secondary-400'
                    : 'text-background-200 hover:bg-primary-900'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}